<script setup lang="ts">
import { computed } from 'vue'
import { get } from 'lodash-unified'
import formBaseTableRender from './form-base-table-render.vue'
import type { FormBaseColumnsItem } from './form-base'

defineOptions({
  name: 'ElFormBaseTableColumnVirtually',
})

const props = defineProps({
  column: Object as any,
  columnsData: {
    type: Array<any>,
    default: () => [],
  },
})

const emit = defineEmits({
  'click-info': (columns: any, row: any) => columns && row,
})

const handleHidden = computed(() => {
  if (typeof props.column.hidden === 'boolean') {
    return props.column.hidden
  }
  return true
})

const handleClick = (columns: FormBaseColumnsItem, row: any) => {
  emit('click-info', columns, row)
}

const getLink = computed(() => {
  if (props.column.isLink || props.column.isDetails) {
    return 'form-base__link__text'
  }
  return ''
})

const isFn = (val: any, row: any, flag = true) => {
  if (typeof val === 'boolean') {
    return val
  }
  if (typeof val === 'string') {
    return val
  }
  if (typeof val === 'function') {
    return val(row)
  }
  return flag
}

const toFormat = (props: any, row: any) => {
  if (row[props.column.prop]) {
    const formatter = isFn(props.column.dateFormatter, row, false)
    const date = formatDate(row[props.column.prop], formatter)
    if (date) {
      return date
    }
    return row[props.column.prop]
  }
  return row[props.column.prop]
}

function formatDate(date: any, fmt = 'yyyy-mm-dd hh:MM:ss') {
  if (!date) {
    return false
  }
  // 如果为null,则格式化当前时间
  date = new Date(date)

  if (date.toString() === 'Invalid Date') {
    return false
  }
  let ret
  const opt: any = {
    'y+': date.getFullYear().toString(),
    'm+': (date.getMonth() + 1).toString(),
    'd+': date.getDate().toString(),
    'h+': date.getHours().toString(),
    'M+': date.getMinutes().toString(),
    's+': date.getSeconds().toString(),
    // 有其他格式化字符需求可以继续添加，必须转化成字符串
  }
  for (const k in opt) {
    ret = new RegExp(`(${k})`).exec(fmt)
    if (ret) {
      fmt = fmt.replace(
        ret[1],
        ret[1].length == 1 ? opt[k] : opt[k].padStart(ret[1].length, '0')
      )
    }
  }
  return fmt
}

const getRowText = (row: any, props: any) => {
  const text = get(row, props)
  return text
}
</script>

<template>
  <VxeColumn
    v-if="handleHidden && !props.column.children"
    :key="props.column.prop"
    :field="props.column.prop"
    :title="props.column.label"
    :width="props.column.width"
    :min-width="props.column.minWidth"
    :align="props.column.align"
    :sortable="props.column.sortable"
    :fixed="props.column.fixed ? props.column.fixed : false"
    :resizable="props.column.resizable"
    :tree-node="props.column.treeNode ? props.column.treeNode : false"
  >
    <template v-if="!props.column.children" #default="scope">
      <template v-if="!props.column.render">
        <template v-if="props.column.dateFormatter">
          {{ toFormat(props, scope.row) }}
        </template>

        <template v-else>
          <template v-if="props.column.renderTooltip">
            <el-tooltip
              :content="
                props.column.renderTooltip(scope.row, columnsData).toString()
              "
              placement="top"
              :open-delay="500"
              effect="dark"
            >
              <span class="el-form-base-tooltip">{{
                getRowText(scope.row, props.column.prop)
              }}</span>
            </el-tooltip>
          </template>
          <template v-else>
            <span
              :class="[getLink]"
              @click="handleClick(props.column, scope.row)"
              >{{ scope.row[props.column.prop] }}</span
            >
          </template>
        </template>
      </template>
      <form-base-table-render
        v-if="props.column.render"
        :row="props.column"
        :scope="scope"
      />
    </template>
    <template v-if="props.column.children && props.column.children.length > 0">
      <el-form-base-table-column-virtually
        v-for="item in props.column.children"
        :key="item.prop"
        :column="item"
        :columnsData="props.columnsData"
      />
    </template>
  </VxeColumn>
  <VxeColgroup
    v-else
    :title="props.column.label"
    :width="props.column.width"
    :min-width="props.column.minWidth"
    :align="props.column.align"
    :sortable="props.column.sortable"
    :fixed="props.column.fixed ? props.column.fixed : false"
    :resizable="props.column.resizable"
  >
    <template v-for="item in props.column.children" :key="item.prop">
      <el-form-base-table-column-virtually
        :column="item"
        :columnsData="props.columnsData"
      />
    </template>
  </VxeColgroup>
</template>
